import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Spinner } from 'components/Elements/Spinner';
import { COOKIE_SESSION_STORAGE_KEY, SESSION_STATUS, setApiToken, verifySession } from 'lib/axios';
import { AUTH_COOKIE_ONLY, EXTERNAL_ADMIN_SCOPE } from 'utils/const';

const AuthContext = createContext(null);

/**
 * 外部管理者スコープを正規化する
 *
 * ロールが解決できない外部管理者は、社内管理者として扱わず最も制限の強い
 * 'products'（商品系のみ）へ倒す。会社名やレガシーフラグには依存しない。
 */
function resolveExternalAdminScope(externalAdminScope, isExternalAdmin) {
  if (
    externalAdminScope === EXTERNAL_ADMIN_SCOPE.PRODUCTS ||
    externalAdminScope === EXTERNAL_ADMIN_SCOPE.ORDERS ||
    externalAdminScope === EXTERNAL_ADMIN_SCOPE.BUYER
  ) {
    return externalAdminScope;
  }

  return isExternalAdmin ? EXTERNAL_ADMIN_SCOPE.PRODUCTS : null;
}

/**
 * localStorage に残したログイン情報 (表示・画面の出し分けに使う値。秘密は入れない) を読む
 */
function readStoredProfile(accessToken) {
  const isExternalAdmin = localStorage.getItem('isExternalAdmin') === 'true';
  const externalAdminScope = resolveExternalAdminScope(
    localStorage.getItem('externalAdminScope'),
    isExternalAdmin,
  );

  return {
    email: localStorage.getItem('email'),
    remember: localStorage.getItem('remember'),
    accessToken,
    isExternalAdmin: isExternalAdmin || externalAdminScope !== null,
    externalAdminScope,
    supplierName: localStorage.getItem('supplierName') || null,
    // 商品系画面で仕入先を自動選択・固定するのに使う（会社名の文字列一致をやめた）
    supplierId: localStorage.getItem('supplierId')
      ? Number(localStorage.getItem('supplierId'))
      : null,
  };
}

function getStoredUser() {
  // 以前は「ログイン状態を保存」のときにパスワードを平文で localStorage に保存していた。
  // 保存はやめたので、残っている端末から起動時に消す
  localStorage.removeItem('password');

  const accessToken = localStorage.getItem('accessToken');
  if (!accessToken) {
    return null;
  }

  // 本対応より前にログイン済みのセッションは isExternalAdmin / externalAdminScope / supplierId を
  // 一切持たない（旧実装が保存していたのは remember / accessToken / password / email / isKaisyoAdmin のみ）。
  // 欠けたまま描画すると外部管理者が社内管理者として扱われたり、仕入先が固定されず会社名も空欄になるため、
  // 半端に補完せずログイン画面へ戻す。login() が完全な値を書き直す。
  if (localStorage.getItem('isExternalAdmin') === null) {
    localStorage.setItem('accessToken', '');
    localStorage.removeItem('isKaisyoAdmin');

    return null;
  }

  setApiToken(accessToken);

  return readStoredProfile(accessToken);
}

/**
 * Cookie だけで認証するとき (AUTH_COOKIE_ONLY) の、確かめに行く前のログイン情報
 *
 * ここで返すのは「API で確かめる候補」で、ログイン中かどうかは AuthProvider が verifySession で決める。
 * Bearer のころに保存したトークンが残っていれば (切り替え前にログインした端末) 消し、
 * 同じログインで API が発行した Cookie が残っていればそのまま使い続けられるよう候補にする。
 */
function getStoredCookieSessionCandidate() {
  localStorage.removeItem('password');

  const hasCookieSession = localStorage.getItem(COOKIE_SESSION_STORAGE_KEY) === 'true';
  const hasLegacyToken = Boolean(localStorage.getItem('accessToken'));
  localStorage.removeItem('accessToken');

  if (!hasCookieSession && !hasLegacyToken) {
    return null;
  }

  // 所属情報が欠けていれば、getStoredUser と同じくログイン画面へ戻す
  if (localStorage.getItem('isExternalAdmin') === null) {
    localStorage.removeItem(COOKIE_SESSION_STORAGE_KEY);
    localStorage.removeItem('isKaisyoAdmin');

    return null;
  }

  return readStoredProfile(null);
}

function clearStoredSession() {
  localStorage.setItem('accessToken', '');
  localStorage.removeItem(COOKIE_SESSION_STORAGE_KEY);
  // 外部管理者の所属情報が端末に残ると、共有端末で次の管理者に引き継がれてしまう
  localStorage.removeItem('isKaisyoAdmin'); // 旧キーの後始末（本対応で書き込みは廃止済み）
  localStorage.setItem('isExternalAdmin', 'false');
  localStorage.setItem('externalAdminScope', '');
  localStorage.setItem('supplierName', '');
  localStorage.setItem('supplierId', '');
}

/**
 * Cookie だけで認証するとき (AUTH_COOKIE_ONLY)、ログインしたのに API が Cookie を発行していなかった
 * (向き先の API で AUTH_COOKIE_ENABLED が無効など)
 */
export class CookieSessionNotEstablishedError extends Error {
  constructor() {
    super('ログインの Cookie を受け取れませんでした。');
    this.name = 'CookieSessionNotEstablishedError';
  }
}

export const AuthProvider = ({ children }) => {
  // Cookie だけで認証するときは、起動時に API で確かめ終わるまで user を決めない
  const [candidate] = useState(() => (AUTH_COOKIE_ONLY ? getStoredCookieSessionCandidate() : null));
  const [user, setUser] = useState(() => (AUTH_COOKIE_ONLY ? null : getStoredUser()));
  const [verifying, setVerifying] = useState(candidate !== null);

  useEffect(() => {
    if (candidate === null) {
      return undefined;
    }

    let cancelled = false;
    verifySession().then((status) => {
      if (cancelled) {
        return;
      }
      if (status === SESSION_STATUS.UNAUTHENTICATED) {
        // 401・419 のときだけ端末の値を消す
        clearStoredSession();
      } else {
        // AUTHENTICATED に加え、UNKNOWN (通信エラー・5xx など一時的な障害) でも端末の値を残したまま
        // ログイン中として画面を出す。一時的な障害でログアウトさせないため。
        // 本当に Cookie が切れていれば、次の API の 401 で lib/axios が端末の値を消してトップへ飛ばす
        // (再試行の画面を出すより単純で、API が戻れば何もせずに使い続けられる)
        localStorage.setItem(COOKIE_SESSION_STORAGE_KEY, 'true');
        setUser(candidate);
      }
      setVerifying(false);
    });

    return () => {
      cancelled = true;
    };
  }, [candidate]);

  const login = useCallback(async (data) => {
    const {
      email,
      remember,
      accessToken,
      isExternalAdmin,
      externalAdminScope,
      supplierName,
      supplierId,
    } = data;

    // Cookie だけで認証するときは、API が Cookie を発行したこと (= 以後の API が通ること) を確かめてから
    // ログイン中にする。401・419 なら (API 側が無効など) ログインさせない。
    // UNKNOWN (通信エラー・5xx など) はログイン中にする: ログインの API 自体は成功して Cookie を受け取っており、
    // 確かめる API の一時的な障害でログインさせないのは行き過ぎ。Cookie が無ければ次の API の 401 でトップへ戻る
    if (AUTH_COOKIE_ONLY && (await verifySession()) === SESSION_STATUS.UNAUTHENTICATED) {
      throw new CookieSessionNotEstablishedError();
    }

    const scope = resolveExternalAdminScope(externalAdminScope, isExternalAdmin);
    const isExternal = Boolean(isExternalAdmin) || scope !== null;

    localStorage.setItem('remember', remember);
    if (AUTH_COOKIE_ONLY) {
      // トークンは HttpOnly の Cookie にあるので保存しない
      localStorage.removeItem('accessToken');
      localStorage.setItem(COOKIE_SESSION_STORAGE_KEY, 'true');
    } else {
      localStorage.setItem('accessToken', accessToken);
    }
    // 旧キーが残った端末で別の管理者がログインすると判定が引き継がれるため、必ず消す
    localStorage.removeItem('isKaisyoAdmin');
    localStorage.setItem('email', email);
    localStorage.setItem('isExternalAdmin', isExternal ? 'true' : 'false');
    localStorage.setItem('externalAdminScope', scope ?? '');
    localStorage.setItem('supplierName', supplierName ?? '');
    localStorage.setItem('supplierId', supplierId != null ? String(supplierId) : '');

    if (!AUTH_COOKIE_ONLY) {
      setApiToken(accessToken);
    }
    setUser({
      ...data,
      accessToken: AUTH_COOKIE_ONLY ? null : accessToken,
      isExternalAdmin: isExternal,
      externalAdminScope: scope,
      supplierName: supplierName ?? null,
      supplierId: supplierId ?? null,
    });
  }, []);

  const logout = useCallback(() => {
    clearStoredSession();
    setUser(null);
    window.location.assign(window.location.origin);
  }, []);

  const value = useMemo(() => ({ user, login, logout }), [user, login, logout]);

  if (verifying) {
    return <Spinner />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
